import { copy } from "../copy";
import Countdown from "../components/Countdown";
import type { ContestDetail, Market } from "./api";
import TierSplit from "../components/TierSplit";
import { primaryButton } from "./ui";

const LABEL_TONE: Record<ContestDetail["state"], string> = {
  draft: "border-ground-edge text-chrome-dim",
  open: "border-money/40 text-money",
  closed: "border-ground-edge text-chrome",
  judging: "border-gold/40 text-gold",
  complete: "border-gold/40 text-gold",
  cancelled: "border-ground-edge text-chrome-dim",
};

// The pool, in money green under the cover so it is the first number on the
// page whichever tab is open. A voided market has no pool to show; the Vote
// tab says why.
function PoolBanner({ market, onPick }: { market: Market; onPick?: () => void }) {
  const p = copy.contest.pool;
  const backers = market.outcomes.reduce((sum, o) => sum + o.backer_count, 0);
  return (
    <div data-testid="contest-pool" className="border-t border-money/30 bg-money/10 px-4 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-money">{p.label}</p>
          <p className="bf-tabular mt-1 font-headline text-5xl leading-none text-money">{market.pool_display}</p>
          <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-chrome-dim">
            {[p.backers(backers), p.fee(market.fee_display)].join(" · ")}
          </p>
          {market.bonus_display && (
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-money">{p.bonus(market.bonus_display)}</p>
          )}
        </div>
        {market.status === "open" && onPick && (
          <button type="button" onClick={onPick} className={primaryButton}>
            {p.cta}
          </button>
        )}
      </div>
    </div>
  );
}

// The cover and the contest's state. Only the countdown's final hour is ever
// alert red; every other state is a chrome or gold label.
export default function CoverPanel({
  contest,
  market,
  onPick,
}: {
  contest: ContestDetail;
  market?: Market | null;
  onPick?: () => void;
}) {
  const c = copy.contest;
  const countdown = contest.state === "open" && contest.show_countdown && contest.ends_at !== "";
  return (
    <div className="overflow-hidden rounded-2xl border border-ground-edge bg-ground-raised">
      <div className="relative aspect-square w-full">
        {contest.cover_url ? (
          <img src={contest.cover_url} alt={c.cover(contest.title)} className="h-full w-full object-cover object-top" />
        ) : (
          <div
            aria-hidden
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ground-edge via-ground-raised to-ground"
          >
            <span className="font-headline text-[10rem] uppercase leading-none text-gold/20">{contest.title.slice(0, 1)}</span>
          </div>
        )}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ground via-transparent to-transparent" />
        <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2">
          <span
            data-testid="state-label"
            className={`rounded-full border bg-ground/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] backdrop-blur-sm ${LABEL_TONE[contest.state]}`}
          >
            {c.states[contest.state]}
          </span>
          {contest.is_seeded && (
            <span
              title={c.seededNote}
              className="rounded-full border border-gold/40 bg-ground/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold backdrop-blur-sm"
            >
              {c.seeded}
            </span>
          )}
        </div>
        {contest.genre && (
          <span className="absolute bottom-3 left-3 rounded-full border border-ground-edge bg-ground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-chrome-dim backdrop-blur-sm">
            {contest.genre}
          </span>
        )}
      </div>
      {market && market.status !== "voided" && <PoolBanner market={market} onPick={onPick} />}
      {market && market.status !== "voided" && market.tier && (
        <div data-testid="contest-tier" className="border-t border-ground-edge px-4 pb-4">
          <TierSplit
            tier={market.tier}
            split={{ rake_bps: market.rake_bps, producer_bps: market.producer_bps, backing_bps: market.backing_bps }}
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ground-edge px-4 py-3">
        {countdown ? (
          <>
            <span className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{c.closesIn}</span>
            <Countdown endsAt={contest.ends_at} />
          </>
        ) : (
          <span data-testid="state-note" className="text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
            {c.stateNote[contest.state]}
          </span>
        )}
      </div>
    </div>
  );
}
